/**
 * ResponsePDF component specifically for handling enquiry responses PDF content.
 * This component manages file downloading and URL fetching for enquiry response attachments.
 */
import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button } from 'carbon-components-react';
import { Download20 } from '@carbon/icons-react';
import {
  getAttachmentPreviewUrlStart,
  resetAttachmentPreviewUrl,
  getAttachmentDownloadUrlStart,
  resetAttachmentDownloadUrl
} from 'redux/enquiries/actions';
import {
  selectAttachmentPreviewUrl,
  selectLoadingAttachmentPreviewUrl,
  selectAttachmentDownloadUrl,
  selectLoadingAttachmentDownloadUrl
} from 'redux/enquiries';
import { getEmailAttachment } from 'utilities/helper.util';
import { useComponentMount } from 'utilities/hooks.util';
import PDFReader from '../PDFReader';

// Forward ref to expose download function to parent components
export interface ResponsePDFRef {
  downloadFile: () => void;
}

interface ResponsePDFProps {
  fileId?: number;
  enquiryId?: number;
  fileUrl?: string;
  fileName?: string;
  viewerContentHeight?: string | number;
  className?: string;
  style?: React.CSSProperties;
  showPageNumbers?: boolean;
  onDocumentLoadSuccess?: (numPages: number) => void;
  onDocumentLoadError?: (error: any) => void;
}

const ResponsePDF = forwardRef<ResponsePDFRef, ResponsePDFProps>(
  (
    {
      fileId,
      enquiryId,
      fileUrl,
      fileName,
      viewerContentHeight,
      className = '',
      style = {},
      showPageNumbers = true,
      onDocumentLoadSuccess,
      onDocumentLoadError
    },
    ref
  ) => {
    const history = useHistory();
    const queryParams = new URLSearchParams(history?.location.search);
    const shareId = queryParams.get('shareId') || '';

    const dispatch = useDispatch();
    const isLoadingPreviewUrl: any = useSelector(
      selectLoadingAttachmentPreviewUrl
    );
    const previewUrl: any = useSelector(selectAttachmentPreviewUrl);
    const isLoadingDownloadUrl: any = useSelector(
      selectLoadingAttachmentDownloadUrl
    );
    const downloadUrl: any = useSelector(selectAttachmentDownloadUrl);

    const [pdfUrl, setPdfUrl] = useState<string>('');
    const [downloadButtonClicked, setDownloadButtonClicked] = useState<boolean>(
      false
    );

    // Only mount Redux effects if using fileId/enquiryId approach
    useComponentMount(() => {
      if (fileId && enquiryId) {
        dispatch(resetAttachmentPreviewUrl());
        dispatch(resetAttachmentDownloadUrl());
      }
    });

    // Handle direct fileUrl usage
    useEffect(() => {
      if (fileUrl && !fileId) {
        setPdfUrl(fileUrl);
      }
    }, [fileUrl, fileId]);

    const downloadFile = () => {
      if (fileId && enquiryId) {
        setDownloadButtonClicked(true);
        dispatch(getAttachmentDownloadUrlStart({ fileId, shareId, enquiryId }));
      }
    };

    // Expose download function to parent components
    useImperativeHandle(ref, () => ({
      downloadFile
    }));

    // Handle download URL response (only for fileId/enquiryId usage)
    useEffect(() => {
      if (
        fileId &&
        enquiryId &&
        !isLoadingDownloadUrl &&
        downloadUrl?.url &&
        downloadButtonClicked
      ) {
        getEmailAttachment({
          url: downloadUrl.url,
          attachmentName: fileName || 'document.pdf'
        });
        setTimeout(() => {
          setDownloadButtonClicked(false);
        }, 2000);
      }
    }, [
      isLoadingDownloadUrl,
      downloadUrl,
      downloadButtonClicked,
      fileName,
      fileId,
      enquiryId
    ]);

    // Handle preview URL response (only for fileId/enquiryId usage)
    useEffect(() => {
      if (fileId && enquiryId && previewUrl && !isLoadingPreviewUrl) {
        setPdfUrl(previewUrl.url);
      }
    }, [isLoadingPreviewUrl, previewUrl, fileId, enquiryId]);

    // Fetch preview URL when component mounts (only for fileId/enquiryId usage)
    useEffect(() => {
      if (fileId && fileId > 0 && enquiryId) {
        dispatch(getAttachmentPreviewUrlStart({ fileId, shareId, enquiryId }));
      }
    }, [fileId, shareId, dispatch, enquiryId]);

    // For fileId/enquiryId usage, show loading while fetching URL
    const showLoading =
      fileId && enquiryId ? isLoadingPreviewUrl || !pdfUrl : !pdfUrl;

    if (showLoading) {
      return (
        <div className={`response-pdf-wrapper ${className}`} style={style}>
          <div className="pdf-loading-message">
            <div>Loading PDF...</div>
          </div>
        </div>
      );
    }

    return (
      <div className={`response-pdf-wrapper ${className}`} style={style}>
        <PDFReader
          fileUrl={pdfUrl}
          viewerContentHeight={viewerContentHeight}
          showPageNumbers={showPageNumbers}
          onDocumentLoadSuccess={onDocumentLoadSuccess}
          onDocumentLoadError={onDocumentLoadError}
          containerClassName="response-pdf-container"
        />
      </div>
    );
  }
);

// Export a download button component for easy use in consumers
export const ResponsePDFDownloadButton: React.FC<{
  responsePDFRef: React.RefObject<ResponsePDFRef>;
  isDownloading?: boolean;
  buttonProps?: any;
}> = ({ responsePDFRef, isDownloading = false, buttonProps = {} }) => {
  const handleDownload = () => {
    responsePDFRef.current?.downloadFile();
  };

  return (
    <Button
      kind="ghost"
      size="field"
      onClick={handleDownload}
      disabled={isDownloading}
      {...buttonProps}
    >
      <Download20 />
    </Button>
  );
};

ResponsePDF.displayName = 'ResponsePDF';

export default ResponsePDF;
